import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { debugLog } from '@/lib/debug'

type Theme = 'light' | 'dark' | 'auto'

interface ThemeContextType {
  theme: Theme
  effectiveTheme: 'light' | 'dark' // The actual theme being used
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Time-based dark mode detection
function getSystemTheme(): 'light' | 'dark' {
  // Dark mode between 8 PM (20:00) and 6 AM (06:00)
  // This is ONLY based on time, not on system preferences
  const hour = new Date().getHours()
  if (hour >= 20 || hour < 6) {
    return 'dark'
  }

  return 'light'
}

function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem('lina-theme')
    debugLog('theme', 'Stored theme from localStorage:', stored)
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored
    }
  } catch (error) {
    console.error('Error reading theme from localStorage:', error)
  }
  return 'auto' // Default to auto
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme)
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() => {
    // Initialize with correct theme immediately
    const stored = getStoredTheme()
    if (stored === 'auto') {
      const system = getSystemTheme()
      debugLog('theme', 'Initial auto mode, using system theme:', system)
      return system
    }
    debugLog('theme', 'Initial theme:', stored)
    return stored
  })

  // Load theme from Supabase on mount
  useEffect(() => {
    const abortController = new AbortController()

    const loadThemeFromDB = async () => {
      try {
        // Add small delay to let AuthContext initialize first
        await new Promise((resolve) => setTimeout(resolve, 100))

        if (abortController.signal.aborted) return

        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!user || abortController.signal.aborted) return

        const { data, error } = await supabase
          .from('user_preferences')
          .select('theme')
          .eq('user_id', user.id)
          .maybeSingle()

        if (abortController.signal.aborted) return

        if (!error && data?.theme) {
          // Map 'system' to 'auto' for compatibility
          const dbTheme = data.theme === 'system' ? 'auto' : (data.theme as Theme)
          debugLog('theme', 'Loaded from DB:', dbTheme)

          // Only update if different from current theme
          const currentTheme = localStorage.getItem('lina-theme')
          if (dbTheme !== currentTheme) {
            debugLog('theme', 'DB theme different from localStorage, updating to:', dbTheme)
            setThemeState(dbTheme)
            localStorage.setItem('lina-theme', dbTheme)
          }
        } else if (!data) {
          // No theme in DB - initialize with default 'auto' (system)
          debugLog('theme', 'No theme in DB, initializing with auto/system')
          const currentTheme = getStoredTheme()
          await supabase.from('user_preferences').upsert(
            {
              user_id: user.id,
              theme: currentTheme === 'auto' ? 'system' : currentTheme,
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: 'user_id',
            },
          )
        } else {
          debugLog('theme', 'Error loading theme:', error)
        }
      } catch (error) {
        // Ignore AbortError - it's expected during cleanup
        if (error instanceof Error && error.name === 'AbortError') return
        console.error('Error loading theme from database:', error)
      }
    }

    loadThemeFromDB()

    return () => {
      abortController.abort()
    }
  }, [])

  // Calculate effective theme based on user preference
  useEffect(() => {
    debugLog('theme', 'Calculating effective theme for:', theme)
    if (theme === 'auto') {
      const systemTheme = getSystemTheme()
      debugLog('theme', 'Auto mode - system theme is:', systemTheme)
      debugLog('theme', 'Current hour:', new Date().getHours())
      setEffectiveTheme(systemTheme)
    } else {
      debugLog('theme', 'Using explicit theme:', theme)
      setEffectiveTheme(theme)
    }
  }, [theme])

  // Apply theme to document
  useEffect(() => {
    debugLog('theme', 'Applying theme to document:', effectiveTheme)
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')
    root.classList.add(effectiveTheme)

    // Also set data attribute for easier CSS selectors
    root.setAttribute('data-theme', effectiveTheme)

    debugLog('theme', 'Document classes:', root.classList.toString())
    debugLog('theme', 'Document data-theme:', root.getAttribute('data-theme'))
  }, [effectiveTheme])

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'auto') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      setEffectiveTheme(getSystemTheme())
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  // Auto-update theme based on time (check every minute when in auto mode)
  useEffect(() => {
    if (theme !== 'auto') return

    const checkTime = () => {
      const newTheme = getSystemTheme()
      if (newTheme !== effectiveTheme) {
        setEffectiveTheme(newTheme)
      }
    }

    // Check every minute
    const interval = setInterval(checkTime, 60000)
    return () => clearInterval(interval)
  }, [theme, effectiveTheme])

  const setTheme = async (newTheme: Theme) => {
    try {
      // Save to localStorage immediately for instant feedback
      localStorage.setItem('lina-theme', newTheme)
      setThemeState(newTheme)

      // Save to Supabase in background
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (user) {
        // Map 'auto' to 'system' for database compatibility
        const dbTheme = newTheme === 'auto' ? 'system' : newTheme

        const { error } = await supabase.from('user_preferences').upsert(
          {
            user_id: user.id,
            theme: dbTheme,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'user_id',
          },
        )

        if (error) {
          console.error('Error saving theme to database:', error)
        }
      }
    } catch (error) {
      console.error('Error saving theme:', error)
    }
  }

  const toggleTheme = () => {
    // Cycle through: light -> dark -> auto -> light
    if (theme === 'light') {
      setTheme('dark')
    } else if (theme === 'dark') {
      setTheme('auto')
    } else {
      setTheme('light')
    }
  }

  return (
    <ThemeContext.Provider value={{ theme, effectiveTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
